/* eslint-disable no-restricted-syntax */

import dagre from '@dagrejs/dagre';
import PropTypes from 'prop-types';

export function generateNode({
  id,
  data,
  x = Math.random() * 600,
  y = Math.random() * 300,
  type = 'product',
}) {
  return ({
    id,
    position: { x, y },
    data,
    type,
    deletable: false,

  });
}

export function getStepsFromFlow(nodes, edges) {
  // Step 1: Build the graph
  const graph = {};
  const inDegree = {};

  nodes.forEach((node) => {
    graph[node.id] = [];
    inDegree[node.id] = 0;
  });

  edges.forEach((edge) => {
    if (!graph[edge.source] || !graph[edge.target]) return; // skip invalid edges
    graph[edge.source].push(edge.target);
    inDegree[edge.target] += 1;
  });

  // Step 2: Find root nodes (no incoming edges)
  const roots = nodes.filter((node) => inDegree[node.id] === 0).map((n) => n.id);

  // Step 3: DFS to find all paths
  const steps = [];

  function dfs(currentId, path) {
    const currentNode = nodes.find((n) => n.id === currentId);
    const newPath = [...path, { id: currentNode.id, ...currentNode.data }];

    const children = graph[currentId];
    if (children.length === 0) {
      steps.push(newPath); // end of path
    } else {
      children.forEach((childId) => dfs(childId, newPath));
    }
  }

  roots.forEach((rootId) => dfs(rootId, []));

  return steps;
}

function constructUniqueId(projectId, pPartId, lineItemId) {
  return `${projectId}-${pPartId}-${lineItemId}`.replaceAll(' ', '');
}

export function groupIntoTreesByLevels(stepsArr) {
  const trees = [];

  for (let branchIdx = 0; branchIdx < stepsArr.length; branchIdx += 1) {
    const branch = stepsArr[branchIdx];
    const partIds = branch.map((step) => step.pPartId).filter(Boolean);

    // Check if this step overlaps with any existing tree
    let matchedTreeIndex = -1;
    for (let treeIdx = 0; treeIdx < trees.length; treeIdx += 1) {
      const tree = trees[treeIdx];
      const flatTree = tree.flat();
      const treePartIds = new Set(flatTree.map((step) => step.pPartId));
      if (partIds.some((id) => treePartIds.has(id))) {
        matchedTreeIndex = treeIdx;
        break;
      }
    }

    if (matchedTreeIndex === -1) {
      // Create a new tree
      const newTree = [];
      branch.forEach((step, stepIdx) => {
        if (!newTree[stepIdx]) newTree[stepIdx] = [];

        const parentStep = branch[stepIdx - 1];
        const childStep = branch[stepIdx + 1];
        const isShippingPoint = step.pPartId === step.targetPPartId;

        newTree[stepIdx].push({
          ...step,
          uniqueId: constructUniqueId(step.projectId, step.pPartId, step.lineItemId),
          parentStep: parentStep === undefined
            ? undefined
            : constructUniqueId(step.projectId, parentStep.pPartId, parentStep.lineItemId),
          childSteps: childStep === undefined
            ? []
            : [constructUniqueId(step.projectId, childStep.pPartId, childStep.lineItemId)],
          isShippingPoint,
          stepIdx,
          lineItemIds: [step.lineItemId],
          goalSum: step.goal,
        });
      });
      trees.push(newTree);
    } else {
      // Merge into existing tree
      const targetTree = trees[matchedTreeIndex];
      branch.forEach((step, stepIdx) => {
        if (!targetTree[stepIdx]) targetTree[stepIdx] = [];
        const isShippingPoint = step.pPartId === step.targetPPartId;

        // Prevent duplicate pPartId in the same level unless it's a shipping point
        if (!targetTree[stepIdx].some((s) => s.pPartId === step.pPartId) || isShippingPoint) {
          const parentStep = branch[stepIdx - 1];
          const targetTreeParentStep = targetTree[stepIdx - 1]?.find(
            (s) => s.pPartId === parentStep?.pPartId,
          );
          const childStep = branch[stepIdx + 1];
          targetTree[stepIdx].push({
            ...step,
            uniqueId: constructUniqueId(step.projectId, step.pPartId, step.lineItemId),
            parentStep: targetTreeParentStep === undefined
              ? undefined
              : constructUniqueId(
                step.projectId,
                targetTreeParentStep.pPartId,
                targetTreeParentStep.lineItemId,
              ),
            childSteps: childStep === undefined
              ? []
              : [constructUniqueId(step.projectId, childStep.pPartId, childStep.lineItemId)],
            isShippingPoint,
            stepIdx,
            lineItemIds: [step.lineItemId],
            goalSum: step.goal,
          });
          // add as new child step to parent
          targetTreeParentStep?.childSteps.push(
            constructUniqueId(step.projectId, step.pPartId, step.lineItemId),
          );
        } else {
          // If it exists, add to lineItemIds
          const existingStep = targetTree[stepIdx].find((s) => s.pPartId === step.pPartId);
          if (existingStep) {
            existingStep.lineItemIds.push(step.lineItemId);
            existingStep.goalSum += step.goal;
          }
        }
      });
    }
  }

  return trees;
}

export const stepType = PropTypes.shape({
  name: PropTypes.string,
  type: PropTypes.string.isRequired,
  pPartId: PropTypes.string,
  pPartDocId: PropTypes.string,
  projectId: PropTypes.string.isRequired,
  projectDocId: PropTypes.string,
  goal: PropTypes.number,
  goalSum: PropTypes.number,
  note: PropTypes.string,
  targetPPartId: PropTypes.string,
  lineItemId: PropTypes.string.isRequired,
  lineItemIds: PropTypes.arrayOf(PropTypes.string),
  stepIdx: PropTypes.number,
  uniqueId: PropTypes.string.isRequired,
  parentStep: PropTypes.string,
  childSteps: PropTypes.arrayOf(PropTypes.string).isRequired,
  isShippingPoint: PropTypes.bool.isRequired,
});

// --- helpers ---
const DEFAULT_NODE_WIDTH = 300;
const DEFAULT_NODE_HEIGHT = 80;

/**
 * Best-effort size probe that matches @xyflow/react / React Flow node shapes.
 * Reads modern `measured`, legacy `__rf.measured`, or style-provided sizes.
 */
function getNodeSize(node) {
  // prefer explicit measured box
  // eslint-disable-next-line no-underscore-dangle
  const measured = node.measured ?? node.__rf?.measured;
  if (measured && measured.width && measured.height) {
    return { width: measured.width, height: measured.height };
  }
  // allow style-based fallback (if you precompute node.style.width/height)
  const styleW = typeof node?.style?.width === 'number' ? node.style.width : undefined;
  const styleH = typeof node?.style?.height === 'number' ? node.style.height : undefined;

  return {
    width: styleW ?? DEFAULT_NODE_WIDTH,
    height: styleH ?? DEFAULT_NODE_HEIGHT,
  };
}

/**
 * Connected components treating edges as undirected for grouping.
 */
function getConnectedComponents(nodes, edges) {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const ids = nodes.map((n) => n.id);
  const idSet = new Set(ids);

  // adjacency list (undirected)
  const adj = new Map(ids.map((id) => [id, []]));
  edges.forEach((e) => {
    if (!idSet.has(e.source) || !idSet.has(e.target)) return;
    adj.get(e.source).push(e.target);
    adj.get(e.target).push(e.source);
  });

  const visited = new Set();
  const groups = [];

  for (const id of ids) {
    // eslint-disable-next-line no-continue
    if (visited.has(id)) continue;
    const stack = [id];
    const group = [];

    while (stack.length) {
      const cur = stack.pop();
      // eslint-disable-next-line no-continue
      if (visited.has(cur)) continue;
      visited.add(cur);
      const n = nodeMap.get(cur);
      // eslint-disable-next-line no-continue
      if (!n) continue;
      group.push(n);
      for (const nxt of adj.get(cur)) {
        if (!visited.has(nxt)) stack.push(nxt);
      }
    }

    groups.push(group);
  }

  return groups;
}

/**
 * Compute bbox in "center coordinates" coming from Dagre, then return top-left bbox
 * (for stacking we only need height; we compute it precisely using width/height).
 */
function computeComponentBBox(nodesInGroup, dagreGraph) {
  let minCX = Infinity;
  let minCY = Infinity;
  let maxCX = -Infinity;
  let maxCY = -Infinity;

  nodesInGroup.forEach((n) => {
    const {
      x, y, width, height,
    } = dagreGraph.node(n.id);
    const left = x - width / 2;
    const right = x + width / 2;
    const top = y - height / 2;
    const bottom = y + height / 2;

    if (left < minCX) minCX = left;
    if (top < minCY) minCY = top;
    if (right > maxCX) maxCX = right;
    if (bottom > maxCY) maxCY = bottom;
  });

  return {
    left: minCX,
    top: minCY,
    right: maxCX,
    bottom: maxCY,
    width: maxCX - minCX,
    height: maxCY - minCY,
  };
}

/**
 * Main: layout per connected component with per-node sizes.
 */
export function getLayoutedElements(nodes, edges, direction = 'LR') {
  const components = getConnectedComponents(nodes, edges);

  const positionedNodes = [];
  const finalEdges = edges;
  let yOffset = 0;
  const COMPONENT_GAP = 50; // vertical gap between components; tweak as needed

  const dir = direction === 'TB' || direction === 'BT' ? direction : 'LR';
  let edgeSrcPos;
  let edgeTgtPos;
  if (dir === 'LR') {
    edgeSrcPos = 'right';
    edgeTgtPos = 'left';
  } else if (dir === 'RL') {
    edgeSrcPos = 'left';
    edgeTgtPos = 'right';
  } else {
    edgeSrcPos = 'bottom';
    edgeTgtPos = 'top';
  }

  // Optimize: build a map {componentId -> Set(nodeIds)} to filter edges quickly
  components.forEach((groupNodes) => {
    const groupIdSet = new Set(groupNodes.map((n) => n.id));

    // Build the Dagre graph for this component
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({
      rankdir: dir,
      ranksep: 80, // vertical spacing between ranks
      nodesep: 40, // horizontal spacing between nodes in same rank
      marginx: 20,
      marginy: 20,
    });

    // Add nodes with their own measured size
    groupNodes.forEach((node) => {
      const { width, height } = getNodeSize(node);
      g.setNode(node.id, { width, height });
    });

    // Add in-group edges only
    edges.forEach((e) => {
      if (groupIdSet.has(e.source) && groupIdSet.has(e.target)) {
        g.setEdge(e.source, e.target);
      }
    });

    // Compute layout
    dagre.layout(g);

    // Compute the component's bounding box using node centers + sizes
    const bbox = computeComponentBBox(groupNodes, g);

    // Convert Dagre center coords to top-left React Flow positions,
    // then normalize to start at (0, yOffset).
    const layoutedGroup = groupNodes.map((node) => {
      const {
        x, y, width, height,
      } = g.node(node.id);
      const topLeftX = x - width / 2;
      const topLeftY = y - height / 2;

      return {
        ...node,
        position: {
          x: (topLeftX - bbox.left), // normalize left to 0
          y: (topLeftY - bbox.top) + yOffset, // stack vertically
        },
        sourcePosition: edgeSrcPos,
        targetPosition: edgeTgtPos,
      };
    });

    positionedNodes.push(...layoutedGroup);

    // Increase yOffset by exact component height + gap (use bbox.height)
    yOffset += bbox.height + COMPONENT_GAP;
  });

  return { nodes: positionedNodes, edges: finalEdges };
}
